// Website-owned access never borrows CarrierFlow credentials or tenant authority.
const ROOT = "/api/site/v1";
export class AccessError extends Error {
  constructor(code, status = 0, requestId) {
    const messages = {
      connection_unavailable:
        "Website access could not connect to its API. Please try again later.",
      site_backend_disabled:
        "Website email access is awaiting activation. Please try again later.",
      site_configuration_required:
        "Website email access is awaiting configuration. Please try again later.",
      network_error:
        "The connection was interrupted. Check your connection and try again.",
      invalid_or_expired_link:
        "This sign-in link has expired or has already been used. Request a new email below.",
      customer_required: "Sign in with a new email link to continue.",
      session_expired: "Your session has ended. Sign in with a new email link.",
      recent_login_required:
        "For this email change, sign in again with a fresh email link first.",
      identity_conflict:
        "That email is already linked to another account. Your current email has not changed.",
      email_unchanged: "Enter a different email address.",
      rate_limited:
        "Please wait before requesting again. Email requests are limited to three per 15 minutes.",
      invalid_input: "Check the fields and try again.",
      preview_expired:
        "This preview has expired. Open a fresh preview to continue.",
      ticket_unavailable:
        "This download link has expired or was already used. Start a fresh download.",
      access_revoked: "Access to this file has been revoked.",
      access_expired: "Your access to this file has expired.",
      access_unavailable: "This file is no longer available.",
      file_unavailable: "This file is not available yet.",
      version_unavailable:
        "This file was replaced. Refresh your library for its current version.",
      grant_required: "This account does not have access to this file.",
      large_file_transport_required:
        "Large-file delivery is not configured yet. Please contact support.",
      partner_access_unavailable: "Partner access is unavailable for this account. Refresh or contact the owner.",
      not_found: "This item is unavailable for your account.",
      account_unavailable: "This account is unavailable. Contact support.",
      demo_identity_required: "Use the demo account's email and password.",
      demo_account_required: "This sign-in is only for demo accounts.",
      forbidden: "This action is unavailable for your account.",
      delivery_ticket_invalid: "This download authorization expired or was used. Start a fresh download.",
      delivery_file_unavailable: "This prepared file is currently unavailable. Refresh the order or contact support.",
      delivery_file_changed: "This file could not be verified. Refresh the order or contact support.",
      delivery_payment_required: "Downloads are unavailable while payment needs confirmation or review.",
      delivery_payment_review: "Downloads are unavailable while payment is under review.",
      delivery_unavailable: "Prepared files have not been released for this account.",
    };
    super(
      messages[code] ||
        "This request could not be completed. Please try again.",
    );
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}
export const operationKey = () => crypto.randomUUID();
export const safeReturn = (value) =>
  (["/customer/access", "/resources", "/customer/access/partners", "/checkout"].includes(value) || /^\/customer\/access\/partners\/[A-Za-z0-9_-]{1,128}$/.test(value || "") || /^\/customer\/access\/enroll\/[A-Za-z0-9_-]{1,128}$/.test(value || ""))
    ? value
    : "/customer/access";
export const queryString = (values) =>
  new URLSearchParams(
    Object.entries(values).filter(([, v]) => v !== "" && v != null),
  ).toString();
export async function siteRequest(
  path,
  { method = "GET", body, key, signal, raw = false, headers = {} } = {},
) {
  if (
    (!/^\/(auth|customer|files)(\/|$)/.test(path) && !/^\/commerce\/orders\/[A-Za-z0-9_-]{1,128}\/delivery(?:\/(?:tickets|download))?$/.test(path)) ||
    path.includes("..") ||
    path.includes("://")
  )
    throw new Error("Invalid website access path");
  let response;
  try {
    response = await fetch(ROOT + path, {
      method,
      credentials: "same-origin",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal,
      headers: {
        ...headers,
        ...(body !== undefined
          ? {
              "Content-Type": "application/json",
              "Idempotency-Key": key || operationKey(),
            }
          : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if (error.name === "AbortError") throw error;
    throw new AccessError("network_error");
  }
  if (
    raw &&
    response.ok &&
    !response.headers.get("content-type")?.includes("text/html")
  )
    return response;
  let result;
  try {
    result = await response.json();
  } catch {
    if (response.status === 401 && /^\/(customer|files)\//.test(path))
      window.dispatchEvent(new Event("site-access-ended"));
    throw new AccessError(
      method === "HEAD" && response.status === 410
        ? "preview_expired"
        : method === "HEAD" && response.status === 401
          ? "session_expired"
          : method === "HEAD" && response.status === 403
            ? "access_unavailable"
            : "connection_unavailable",
      response.status || 503,
    );
  }
  if (!response.ok || !result || !("data" in result)) {
    if (response.status === 401 && /^\/(customer|files)\//.test(path))
      window.dispatchEvent(new Event("site-access-ended"));
    throw new AccessError(
      result?.error?.code || "connection_unavailable",
      response.status,
      result?.requestId,
    );
  }
  return result;
}
export async function downloadFile(
  resource,
  { signal, onProgress = () => {}, delivery } = {},
) {
  let writable;
  try {
    // The picker must open from the click, before awaiting an API request.
    if (typeof window.showSaveFilePicker === "function") {
      const handle = await window.showSaveFilePicker({
        suggestedName: resource.file.name,
      });
      writable = await handle.createWritable();
    }
    const { data } = await siteRequest(delivery ? delivery + "/tickets" : "/files/tickets", {
      method: "POST",
      body: delivery ? { fileId: resource.file.id } : { resourceId: resource.id, purpose: "download" },
      signal,
    });
    const response = await siteRequest(delivery ? delivery + "/download" : "/files/download", {
      method: "POST",
      body: { ticket: data.ticket },
      signal,
      raw: true,
    });
    if (
      !["application/pdf", "application/zip"].includes(
        response.headers.get("content-type")?.split(";")[0],
      )
    )
      throw new AccessError("connection_unavailable");
    if (!response.body) throw new AccessError("network_error");
    const reader = response.body.getReader(),
      chunks = [];
    let total = 0;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        total += value.byteLength;
        if (total > 50 * 1024 * 1024) throw new AccessError("file_unavailable");
        if (writable) await writable.write(value);
        else chunks.push(value);
        onProgress(total);
      }
    } catch (error) {
      await reader.cancel().catch(() => {});
      throw error;
    }
    if (total !== resource.file.size)
      throw new AccessError("version_unavailable");
    if (writable) {
      await writable.close();
      writable = null;
    } else {
      // Bounded fallback for Safari/Firefox: up to the server's 50 MiB limit.
      const url = URL.createObjectURL(
        new Blob(chunks, { type: resource.file.mime }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = resource.file.name;
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
    return total;
  } catch (error) {
    if (writable) await writable.abort().catch(() => {});
    throw error;
  }
}
